import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AdminHeader from "../../Components/AdminHeader/AdminHeader";
import AdminNav from "../../Components/AdminNav/AdminNav";
import 'react-quill/dist/quill.snow.css';
import './Vacancies.css';
// import { getNewsPostById, updateNewsPost } from "../../actions/NewsPages";
import {IMAGE_URL} from "../../path";
import {getVacancyById, updateVacancy} from "../../actions/Vacancies";

interface FormData {
    title: string;
    subtitle: string;
    year_posted: string;
    image: File | null;
    content: string;
    content_2: string;
    template_id?: number;
    [key: string]: any; // For dynamic body image fields
}

function EditVacancy() {
    const location = useLocation();
    const { template, id } = useParams<{ template?: string; id?: string }>();

    const navigate = useNavigate();

    if (location === null || location.state === null || location.state.user === null) {
        navigate('/login');
    }

    const user = location.state?.user;
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [errors, setErrors] = useState<string[]>([]);
    const [successMessage, setSuccessMessage] = useState<string>("");
    const [formData, setFormData] = useState<FormData>({
        title: "",
        subtitle: "",
        year_posted: "",
        image: null,
        content: "",
        content_2: "",
        template_id: template ? parseInt(template) : undefined,
    });

    const [coverPhotoUrl, setCoverPhotoUrl] = useState<string>("");
    const [bodyImageUrls, setBodyImageUrls] = useState<{ [key: string]: string }>({});

    const ReactQuill = require('react-quill');
    const modules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'align': [] }],
            ['link', 'image'],
            ['clean'],
        ],
    };

    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => {
                setSuccessMessage("");
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    useEffect(() => {
        const fetchVacancy = async () => {
            try {
                if (!id) return;

                const response = await getVacancyById(Number(id));
                const data = response?.data.data;


                if (data) {

                    // Set body image URLs for preview
                    const bodyUrls: { [key: string]: string } = {};
                    Array.from({ length: 27 }, (_, i) => i + 1).forEach(i => {
                        const imagePath = data[`image_path_body_${i}`];
                        if (imagePath) {
                            bodyUrls[`image_path_body_${i}`] = imagePath;
                        }
                    });



                    setFormData(prev => ({
                        ...prev,
                        title: data.title || "",
                        subtitle: data.subtitle || "",
                        year_posted: data.year_posted || "",
                        image: null,
                        content: data.content || "",
                        content_2: data.content_2 || "",
                        template_id: data.template_id,
                        ...Object.fromEntries(
                            Array.from({ length: 27 }, (_, i) => i + 1)
                                .map(i => [
                                    [`image_path_body_${i}`, null],
                                    [`image_path_body_${i}_caption`, data[`image_path_body_${i}_caption`] || ""],
                                ])
                                .flat()
                        ),
                    }));

                    // Set cover photo URL if available
                    if (data.image_path) {
                        setCoverPhotoUrl(data.image_path);
                    }

                    setBodyImageUrls(bodyUrls);
                }
            } catch (error) {
                console.error("Error fetching news post:", error);
                setErrors(["Failed to load news post."]);
            }
        };

        fetchVacancy();
    }, [id]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value, type } = e.target;

        if (type === "file") {
            const fileInput = e.target as HTMLInputElement;
            const file = fileInput.files?.[0] || null;
            setFormData(prev => ({ ...prev, [name]: file }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleQuillChange = (value: string, name: string) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setErrors([]);
        setSuccessMessage("");

        // For now leaving it commented out (you can enable this when your update API is ready)
        try {
            const formDataToSend = new FormData();

            Object.keys(formData).forEach(key => {
                if (formData[key] !== null && formData[key] !== undefined) {
                    formDataToSend.append(key, formData[key]);
                }
            });

            if (template) {
                formDataToSend.append('template_id', template);
            }

            const response = await updateVacancy(formDataToSend, Number(id));

            if (response.data?.success) {
                setSuccessMessage("News post updated successfully!");
                navigate('/dashboard/vacancies/list', { state: { user } });
            } else {
                setErrors([response.data?.message || "An unexpected error occurred"]);
            }
        } catch (error: any) {
            console.error("Error submitting form:", error);
            if (error.response?.data?.errors) {
                const errorMessages = Object.values(error.response.data.errors).flat();
                setErrors(errorMessages as string[]);
            } else {
                setErrors(["An error occurred while submitting the form"]);
            }
        }
    };

    const renderBodyImageFields = () => {
        if (!template) return null;

        const templateNum = parseInt(template);
        const imageFields = [];

        let imageCount = 0;
        if (templateNum === 1) imageCount = 27;
        else if (templateNum === 2) imageCount = 1;
        else if (templateNum === 3) imageCount = 2;

        for (let i = 1; i <= imageCount; i++) {
            imageFields.push(
                <div key={i} style={{ padding: '10px', boxSizing: 'border-box' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <label className="control-label">Body Image {i}</label>
                        {bodyImageUrls[`image_path_body_${i}`] && (
                            <img
                                src={IMAGE_URL + bodyImageUrls[`image_path_body_${i}`]}
                                alt={`Body Image ${i}`}
                                style={{ width: '100%', maxHeight: '150px', objectFit: 'cover', marginBottom: '5px' }}
                            />
                        )}
                        <input
                            type="file"
                            id={`image_path_body_${i}`}
                            className="form-control"
                            name={`image_path_body_${i}`}
                            onChange={handleInputChange}
                        />
                        <input
                            type="text"
                            id={`image_path_body_${i}_caption`}
                            className="form-control"
                            name={`image_path_body_${i}_caption`}
                            placeholder="caption"
                            value={formData[`image_path_body_${i}_caption`] || ""}
                            onChange={handleInputChange}
                        />
                    </div>
                </div>
            );
        }

        return (
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '15px',
                }}
            >
                {imageFields}
            </div>
        );
    };

    return (
        <div>
            <AdminHeader isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} user={user} />
            <AdminNav collapsed={isCollapsed} setCollapsed={setIsCollapsed} user={user} />

            <div className={`main-admin-page ${isCollapsed ? "collapsed" : "expanded"}`}>
                <div className="row">
                    <div className="col-md-12 col-sm-12 col-xs-12">
                        <div className="x_panel">
                            <div className="x_title">
                                <h2>Edit Vacancy {template ? `Template ${template}` : ''}</h2>
                            </div>
                            <div className="notification-box">
                                {errors.length > 0 && (
                                    <div className="alert alert-danger">
                                        <ul>
                                            {errors.map((error, index) => (
                                                <li key={index}>{error}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {successMessage && (
                                    <div className="alert alert-success">
                                        <i className="fa fa-check"></i>{successMessage}
                                    </div>
                                )}
                            </div>

                            <div className="x_content">
                                <div className="row">
                                    <div className="col-md-9 col-xs-12">
                                        <div className="x_panel">
                                            <div className="x_content">
                                                <br />
                                                <form
                                                    className="form-horizontal form-label-left"
                                                    onSubmit={handleSubmit}
                                                    encType="multipart/form-data"
                                                    style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
                                                >
                                                    <input
                                                        type="text"
                                                        className="form-control news_title"
                                                        name="title"
                                                        value={formData.title}
                                                        onChange={handleInputChange}
                                                        required
                                                        placeholder="Title"
                                                    />

                                                    <div>
                                                        <label className="control-label">Subtitle</label>
                                                        <textarea
                                                            value={formData.subtitle}
                                                            onChange={handleInputChange}
                                                            name="subtitle"
                                                            className="form-control news_subtitle"
                                                            required
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="control-label" style={{ marginRight: '20px' }}>Date</label>
                                                        <input
                                                            type="date"
                                                            className="form-control year_posted"
                                                            name="year_posted"
                                                            value={formData.year_posted}
                                                            onChange={handleInputChange}
                                                            required
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="control-label" style={{ marginRight: '20px' }}>Cover Photo</label>
                                                        {coverPhotoUrl && (
                                                            <img
                                                                src={IMAGE_URL + coverPhotoUrl}
                                                                alt="Cover"
                                                                style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', marginBottom: '5px' }}
                                                            />
                                                        )}
                                                        <input
                                                            type="file"
                                                            id="image"
                                                            className="form-control news_image"
                                                            name="image"
                                                            ref={fileInputRef}
                                                            onChange={handleInputChange}
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="control-label">Content</label>
                                                        <ReactQuill
                                                            value={formData.content}
                                                            onChange={(value: string) => handleQuillChange(value, 'content')}
                                                            modules={modules}
                                                            theme="snow"
                                                            style={{ minHeight: '200px' }}
                                                        />
                                                    </div>

                                                    {template && (
                                                        <div>
                                                            {renderBodyImageFields()}
                                                        </div>
                                                    )}

                                                    {template && (
                                                        <div>
                                                            <label className="control-label">Content 2</label>
                                                            <ReactQuill
                                                                value={formData.content_2}
                                                                onChange={(value: string) => handleQuillChange(value, 'content_2')}
                                                                modules={modules}
                                                                theme="snow"
                                                                style={{ minHeight: '200px' }}
                                                            />
                                                        </div>
                                                    )}

                                                    <div className="ln_solid"></div>
                                                    <div>
                                                        <button type="submit" className="btn btn-success">
                                                            Submit
                                                        </button>
                                                    </div>
                                                </form>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default EditVacancy;
